// "Compose new ticket" — initiates an outbound email + creates a ticket.
//
// Posts to workflow 15 (`/webhook/create-ticket`). Different from
// ReplyComposer in that there's no parent ticket: we're starting a thread
// from scratch. When the customer replies, workflow 01 sees the
// gmail_thread_id workflow 15 wrote and threads it back to this ticket.
//
// Affordances we keep from ReplyComposer:
//   - Templates slash-menu (typing `/` at start of a paragraph)
//   - 🪄 Polish button (Edge Function rewrites the user's draft)
//   - To/Cc/Bcc chip inputs
//   - Idempotency: per-click UUID short-circuits double-sends
//   - Per-user signature appended server-side
//
// Deliberately omitted from v1:
//   - ✨ Draft button (it's "draft a reply from existing ticket context";
//     no context here yet — could add a "draft a follow-up to <contact>"
//     variant later if useful)
//   - Quoted-reply prefill (nothing to quote — fresh thread)
//   - localStorage autosave (single dialog session is the typical use;
//     can revisit if compose-and-abandon becomes common)

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EmailChipsInput } from '@/components/EmailChipsInput';
import { AttachmentsRow } from '@/components/AttachmentsRow';
import {
  RichBodyEditor,
  consumePendingImages,
  findInlineImageIds,
  registerPendingImages,
  type RichBodyEditorHandle,
} from '@/components/RichBodyEditor';
import { htmlToPlain } from '@/lib/html-normalize';
import { firstNameFrom, renderTemplateForInsert } from '@/lib/template-render';
import { enqueueCompose, takeJob } from '@/lib/outbox-jobs';
import { onRestoreRequest } from '@/lib/outbox-restore';
import type { ComposeOutboxPayload } from '@/lib/outbound-send';
import { RichBodyToolbar } from '@/components/RichBodyToolbar';
import { useAiAssist, useAiBarOpen } from '@/lib/use-ai-assist';
import { AiAssistBar } from '@/components/AiAssistBar';
import {
  useReplyTemplates,
  useContactSuggestions,
  type ReplyTemplate,
} from '@/lib/queries';
import { parseInboxPath } from '@/lib/inbox-scope';
import { useViewableInboxes } from '@/lib/use-access';
import { useComposerAttachments } from '@/lib/use-composer-attachments';
import { Paperclip, Send, Sparkles, SlidersHorizontal, Wand2, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';

type Priority = 'P1' | 'P2' | 'P3' | 'P4';
const PRIORITIES: Priority[] = ['P1', 'P2', 'P3', 'P4'];
const CATEGORIES = [
  'billing',
  'technical',
  'onboarding',
  'feedback',
  'sales',
  'other',
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill recipient (e.g. "Compose to this contact" from a contact page). */
  prefillTo?: string;
}

// Compose dialog autosaves to localStorage so content survives any kind of
// reset — tab-switch-induced state clear, accidental dialog close, browser
// reload mid-typing, etc. Same pattern as ReplyComposer's draft autosave.
// Keyed off a single bucket since there's only ever one Compose dialog open
// at a time (unlike per-ticket reply drafts).
const COMPOSE_DRAFT_KEY = 'pa-compose-draft';
const COMPOSE_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
interface ComposeDraft {
  savedAt: number;
  to: string[];
  cc: string[];
  bcc: string[];
  showCc: boolean;
  showBcc: boolean;
  subject: string;
  body: string;
  priority: Priority;
  category: string;
}
function loadComposeDraft(): ComposeDraft | null {
  try {
    const raw = localStorage.getItem(COMPOSE_DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as ComposeDraft;
    if (typeof d?.savedAt !== 'number' || Date.now() - d.savedAt > COMPOSE_DRAFT_TTL_MS) {
      localStorage.removeItem(COMPOSE_DRAFT_KEY);
      return null;
    }
    return d;
  } catch {
    return null;
  }
}
function saveComposeDraft(d: Omit<ComposeDraft, 'savedAt'>) {
  try {
    // Don't pollute storage with empty drafts (defaults).
    const empty =
      d.to.length === 0 &&
      d.cc.length === 0 &&
      d.bcc.length === 0 &&
      d.subject.trim().length === 0 &&
      d.body.trim().length === 0;
    if (empty) {
      localStorage.removeItem(COMPOSE_DRAFT_KEY);
      return;
    }
    localStorage.setItem(COMPOSE_DRAFT_KEY, JSON.stringify({ ...d, savedAt: Date.now() }));
  } catch {
    /* localStorage can throw in private mode / quota — best effort */
  }
}
function clearComposeDraft() {
  try { localStorage.removeItem(COMPOSE_DRAFT_KEY); } catch { /* ignore */ }
}

export function ComposeDialog({ open, onOpenChange, prefillTo }: Props) {
  const qc = useQueryClient();
  // "Follow the current inbox": ComposeDialog is mounted globally in TopNav,
  // so it reads the inbox from the URL (the inbox the agent is viewing).
  // When on a non-inbox page there's no slug → inbox_id stays undefined and
  // workflow 15 falls back to the primary inbox. We deliberately do NOT use
  // the last-visited fallback here — composing from a global page should be a
  // primary-inbox ticket, not silently inherit the last inbox visited.
  const { pathname } = useLocation();
  // Only inboxes where this person holds tickets.create (migration 078); the
  // server checks again. Not in a create-able inbox → the primary one (the
  // old default, sent as undefined), else the first they can create in.
  const composeInboxesQ = useViewableInboxes('tickets.create');
  const composeInbox = useMemo(() => {
    const list = composeInboxesQ.data;
    const slug = parseInboxPath(pathname).slug;
    const here = slug ? list.find((i) => i.label_namespace === slug) : undefined;
    if (here) return { id: here.id as string | undefined, name: here.display_name };
    const primary = list.find((i) => i.is_primary);
    if (primary) return { id: undefined, name: primary.display_name };
    const first = list[0];
    return first ? { id: first.id as string | undefined, name: first.display_name } : null;
  }, [pathname, composeInboxesQ.data]);
  const composeInboxId = composeInbox?.id;
  // Hydrate from localStorage on mount so any state-clearing scenario
  // (tab switch, accidental remount) restores transparently. Empty
  // defaults if no draft exists or it's expired.
  const initialDraft = useMemo(() => loadComposeDraft(), []);
  const [to, setTo] = useState<string[]>(initialDraft?.to ?? []);
  const [cc, setCc] = useState<string[]>(initialDraft?.cc ?? []);
  const [bcc, setBcc] = useState<string[]>(initialDraft?.bcc ?? []);
  const [showCc, setShowCc] = useState(initialDraft?.showCc ?? false);
  const [showBcc, setShowBcc] = useState(initialDraft?.showBcc ?? false);

  // Contacts autocomplete — one query string + suggestion list per field.
  const [toQuery, setToQuery] = useState('');
  const [ccQuery, setCcQuery] = useState('');
  const [bccQuery, setBccQuery] = useState('');
  const toSuggestions = useContactSuggestions(toQuery, to);
  const ccSuggestions = useContactSuggestions(ccQuery, cc);
  const bccSuggestions = useContactSuggestions(bccQuery, bcc);
  const [subject, setSubject] = useState(initialDraft?.subject ?? '');
  const [body, setBody] = useState(initialDraft?.body ?? '');
  const [priority, setPriority] = useState<Priority>(initialDraft?.priority ?? 'P3');
  const [category, setCategory] = useState(initialDraft?.category ?? 'other');

  const attachments = useComposerAttachments();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composeSessionRef = useRef<string>(crypto.randomUUID());

  // Persist on every change so the latest content is always recoverable.
  useEffect(() => {
    saveComposeDraft({ to, cc, bcc, showCc, showBcc, subject, body, priority, category });
  }, [to, cc, bcc, showCc, showBcc, subject, body, priority, category]);

  // NOTE: removed the previous "reset on close" effect. It used to wipe
  // state when `open` flipped false, but that handed the modal-disappears
  // bug a guaranteed way to also nuke the user's typed content. Now state
  // is only reset when Send queues the email (send() below clears via
  // attachments.clear() + clearComposeDraft + setBody('') etc), which is
  // the only reset path that's actually user-intentional.

  // Apply prefillTo whenever it changes (and dialog is opening).
  useEffect(() => {
    if (open && prefillTo) {
      setTo([prefillTo.toLowerCase()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Templates slash-menu — same UX pattern as ReplyComposer.
  const templatesQ = useReplyTemplates();
  const templates = templatesQ.data ?? [];
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuQuery, setMenuQuery] = useState('');
  const [menuActiveIdx, setMenuActiveIdx] = useState(0);
  const [slashStart, setSlashStart] = useState<number | null>(null);
  const editorRef = useRef<RichBodyEditorHandle>(null);

  const matches = useMemo(() => {
    if (!menuOpen) return [];
    const q = menuQuery.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => t.name.toLowerCase().includes(q));
  }, [menuOpen, menuQuery, templates]);

  function detectSlash(text: string, caret: number): number | null {
    for (let i = caret - 1; i >= 0; i--) {
      const ch = text[i];
      if (ch === '/') {
        const prev = i === 0 ? '\n' : text[i - 1];
        if (prev === '\n') return i;
        return null;
      }
      if (ch === '\n' || ch === ' ') return null;
    }
    return null;
  }

  // Plain-text caret offset inside the editor's innerText. Mirrors
  // ReplyComposer.getEditorCaretOffset.
  function getEditorCaretOffset(): number | null {
    const el = editorRef.current?.getElement();
    if (!el) return null;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.startContainer)) return null;
    const before = document.createRange();
    before.setStart(el, 0);
    before.setEnd(range.startContainer, range.startOffset);
    return before.toString().length;
  }

  // Replace `/<query>` before the caret with the template body. Same
  // pattern as ReplyComposer.selectTemplate.
  function applyTemplate(tpl: ReplyTemplate) {
    if (slashStart === null) return;
    const editor = editorRef.current;
    const el = editor?.getElement();
    if (!editor || !el) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.startContainer)) return;

    const text = editor.getText();
    const caretOffset = getEditorCaretOffset() ?? text.length;
    const charsToDelete = Math.max(0, caretOffset - slashStart);
    el.focus();
    for (let i = 0; i < charsToDelete; i++) {
      sel.modify('extend', 'backward', 'character');
    }
    document.execCommand('delete');
    // Was `plainToHtmlParagraphs(tpl.body)`, which ESCAPES its input. Since
    // SaveAsTemplateDialog stores the composer's raw HTML, most templates
    // contain real tags — so inserting one here rendered the markup as visible
    // literal text and shipped it to the client. ReplyComposer fixed this
    // months ago; ComposeDialog never got the fix. Both now share
    // `renderTemplateForInsert`, which passes HTML through and only wraps
    // genuine plain text. It also fills the `{{...}}` variables, which this
    // path silently ignored.
    document.execCommand(
      'insertHTML',
      false,
      renderTemplateForInsert(tpl.body, {
        firstName: firstNameFrom(null, to[0]),
        ticketSubject: subject,
        senderEmail: to[0] ?? '',
        ticketIdShort: '',
      }),
    );

    setMenuOpen(false);
    setSlashStart(null);
    setMenuQuery('');
    setBody(editor.getHtml());
  }

  // Editor onChange — fires with innerHTML. Mirror state, then check the
  // plain-text content for a slash trigger.
  function onBodyChange(html: string) {
    setBody(html);
    const editor = editorRef.current;
    if (!editor) return;
    const text = editor.getText();
    const caret = getEditorCaretOffset() ?? text.length;
    const slashIdx = detectSlash(text, caret);
    if (slashIdx !== null) {
      setSlashStart(slashIdx);
      setMenuQuery(text.slice(slashIdx + 1, caret));
      setMenuOpen(true);
      setMenuActiveIdx(0);
    } else if (menuOpen) {
      setMenuOpen(false);
      setSlashStart(null);
    }
  }

  // Toolbar commands fire execCommand which mutates the editor's HTML
  // synchronously, but the editor's `onChange` only fires from user input
  // events. Pull the current HTML and run it through the same handler so
  // autosave + slash-menu state stay consistent after a B/I/U click.
  function syncBodyFromEditor() {
    const editor = editorRef.current;
    if (!editor) return;
    onBodyChange(editor.getHtml());
  }

  function onEditorKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (menuOpen && matches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMenuActiveIdx((i) => Math.min(matches.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMenuActiveIdx((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        applyTemplate(matches[menuActiveIdx]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMenuOpen(false);
        return;
      }
    }
    // Cmd/Ctrl+Enter to submit
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void send();
    }
  }

  // AI Draft / Polish. Same hook as the reply composer. There is no ticket yet,
  // so Draft works from the instruction (and any rough notes already typed) and
  // Polish rewrites the body. The hook keeps inline images + links and provides
  // a real Undo.
  const assist = useAiAssist({
    recipient: to.join(', '),
    getBodyHtml: () => body,
    applyHtml: (html) => {
      editorRef.current?.setHtml(html);
      setBody(html);
    },
  });
  const [aiBarOpen, toggleAiBar] = useAiBarOpen();
  const isBodyEmpty = htmlToPlain(body).trim().length === 0 && !/<img\b/i.test(body);

  // Send queues the email in the app-wide outbox (lib/send-outbox.ts) and
  // closes the dialog at once. The outbox owns the 5 s undo window (a brand-new
  // outbound email to someone who has never written to us is the least
  // recoverable send in the app, so it keeps one), the uploads, the send, the
  // retry and the toasts — none of which needs this dialog to stay open, or the
  // person to stay on this page. A failure comes back as a persistent toast
  // with Retry and Edit; Undo / Edit reopen this dialog with everything as it
  // was (see the restore handler below).
  function send() {
    if (to.length === 0) return toast.error('At least one To recipient is required.');
    if (subject.trim().length === 0) return toast.error('Subject is required.');
    // Body validation: empty when both the plain-text projection AND any
    // inline images are missing. An image-only message is still a valid
    // send (matches Gmail's behavior).
    const plainEmpty = htmlToPlain(body).trim().length === 0;
    const noImages = !/<img\b/i.test(body);
    if (plainEmpty && noImages) return toast.error('Type a message before sending.');

    // Snapshot everything the send needs, right now.
    const payload: ComposeOutboxPayload = {
      // Per click, and reused by every retry — workflow 15 answers `deduped`
      // instead of creating a second ticket.
      idempotency_key: crypto.randomUUID(),
      inbox_id: composeInboxId,
      to,
      cc,
      bcc,
      subject,
      priority,
      category,
      snapshot: {
        // Compose has no ticket_id yet; files go under a per-session uuid.
        bucket: `compose-${composeSessionRef.current}`,
        bodyHtml: body,
        files: attachments.items.map((it) => ({ localId: it.localId, file: it.file })),
        inlineImages: consumePendingImages(findInlineImageIds(body)),
        uploaded: new Map(),
      },
    };
    enqueueCompose(qc, payload, {
      fromPathname: pathname,
      currentPathname: () => window.location.pathname,
    });

    // Close FIRST, then reset. The reset is non-critical "clean slate for the
    // next open" bookkeeping — if any of it throws it must not strand the
    // modal open.
    onOpenChange(false);
    try {
      setTo([]);
      setCc([]);
      setBcc([]);
      setShowCc(false);
      setShowBcc(false);
      setSubject('');
      setBody('');
      editorRef.current?.setHtml('');
      setPriority('P3');
      setCategory('other');
      attachments.clear();
      clearComposeDraft();
      composeSessionRef.current = crypto.randomUUID();
    } catch (e) {
      console.warn('Compose reset after send failed (non-critical):', e);
    }
  }

  // ── Taking a queued email back (Undo toast / Edit on a failure) ──────────
  // Refill the form from the outbox's snapshot and reopen the dialog. The
  // editor mounts from `body` (initialHtml) when the dialog opens, so setting
  // state is enough even while it is closed.
  const hasContentRef = useRef(false);
  hasContentRef.current =
    to.length > 0 || subject.trim().length > 0 || !isBodyEmpty || attachments.items.length > 0;
  const restoreFrom = (p: ComposeOutboxPayload) => {
    setTo(p.to);
    setCc(p.cc);
    setBcc(p.bcc);
    setShowCc(p.cc.length > 0);
    setShowBcc(p.bcc.length > 0);
    setSubject(p.subject);
    setPriority(p.priority);
    setCategory(p.category);
    setBody(p.snapshot.bodyHtml);
    editorRef.current?.setHtml(p.snapshot.bodyHtml);
    registerPendingImages(p.snapshot.inlineImages);
    if (p.snapshot.files.length > 0) attachments.add(p.snapshot.files.map((f) => f.file));
  };
  const restoreFromRef = useRef(restoreFrom);
  restoreFromRef.current = restoreFrom;
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  useEffect(
    () =>
      onRestoreRequest('compose', (job) => {
        if (
          hasContentRef.current &&
          !window.confirm('Replace what you have typed in the new ticket form with this email?')
        ) {
          return false;
        }
        const taken = takeJob<ComposeOutboxPayload>(job.id);
        if (!taken) return false;
        restoreFromRef.current(taken.payload);
        onOpenChangeRef.current(true);
        return true;
      }),
    [],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-[95vw] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Compose new ticket</DialogTitle>
          <DialogDescription>
            Send the first email to a customer. A new ticket will be created;
            their reply threads back automatically.
            {composeInbox?.name ? ` It will be created in ${composeInbox.name}.` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <EmailChipsInput
            label="To:"
            values={to}
            onChange={setTo}
            placeholder="customer@example.com"
            autoFocus
            onDraftChange={setToQuery}
            suggestions={toSuggestions}
            rightAction={
              <div className="ml-auto flex items-center gap-2 pr-1">
                {!showCc ? (
                  <button
                    type="button"
                    onClick={() => setShowCc(true)}
                    className="text-2xs text-muted-foreground hover:text-foreground"
                  >
                    Add Cc
                  </button>
                ) : null}
                {!showBcc ? (
                  <button
                    type="button"
                    onClick={() => setShowBcc(true)}
                    className="text-2xs text-muted-foreground hover:text-foreground"
                  >
                    Add Bcc
                  </button>
                ) : null}
              </div>
            }
          />

          {showCc ? (
            <EmailChipsInput
              label="Cc:"
              values={cc}
              onChange={setCc}
              placeholder="cc@example.com"
              onDraftChange={setCcQuery}
              suggestions={ccSuggestions}
            />
          ) : null}
          {showBcc ? (
            <EmailChipsInput
              label="Bcc:"
              values={bcc}
              onChange={setBcc}
              placeholder="bcc@example.com"
              onDraftChange={setBccQuery}
              suggestions={bccSuggestions}
            />
          ) : null}

          {/* Subject */}
          <div className="grid gap-1">
            <Label htmlFor="compose-subject" className="text-2xs uppercase tracking-wide text-muted-foreground">
              Subject
            </Label>
            <Input
              id="compose-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What's this about?"
              className="h-9 text-sm"
            />
          </div>

          {/* Priority + Category — small inline pickers, defaults are sensible. */}
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1">
              <Label className="text-2xs uppercase tracking-wide text-muted-foreground">
                Priority
              </Label>
              <div className="flex gap-1">
                {PRIORITIES.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={cn(
                      'h-8 flex-1 cursor-pointer rounded-md border text-xs font-medium transition-colors',
                      priority === p
                        ? 'border-transparent bg-foreground text-background'
                        : 'border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-1">
              <Label htmlFor="compose-category" className="text-2xs uppercase tracking-wide text-muted-foreground">
                Category
              </Label>
              <select
                id="compose-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="h-8 rounded-md border border-border bg-background px-2 text-xs"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Body. Rich contentEditable with the same formatting toolbar
              the reply composer has — bold/italic/underline/strike/code,
              bulleted + numbered lists, indent/outdent, quote, link,
              clear-formatting. Pasted images embed inline at the caret
              (Gmail-style); non-image files bubble to onPasteFiles which
              the attachments hook then appends. Drag/drop on this wrapper
              attaches files dropped anywhere on the body region. */}
          <div className="relative grid gap-1" {...attachments.dragHandlers}>
            <Label className="text-2xs uppercase tracking-wide text-muted-foreground">
              Message · type <kbd className="rounded border border-border/60 bg-muted px-1 py-0.5 font-mono">/</kbd> for templates · paste / drag images to embed inline
            </Label>
            <div className="rounded-md border border-border/70 bg-background">
              <RichBodyEditor
                ref={editorRef}
                initialHtml={body}
                onChange={onBodyChange}
                onPasteFiles={(files) => attachments.add(files)}
                onKeyDown={onEditorKeyDown}
                placeholder="Hi …"
                ariaLabel="Compose body"
              />
              {aiBarOpen ? (
                <AiAssistBar
                  assist={assist}
                  hasBodyText={!isBodyEmpty}
                  isCompose
                  disabled={false}
                />
              ) : null}
              {/* Formatting toolbar lives BELOW the editor — same
                  ergonomic reason as ReplyComposer. The emoji popover
                  flips to open upward automatically when position="bottom"
                  (see RichBodyToolbar). */}
              <RichBodyToolbar
                editorRef={editorRef}
                onCommand={syncBodyFromEditor}
                position="bottom"
              />
              {attachments.items.some((it) => it.previewUrl === null) ? (
                <div className="border-t border-border/40 px-3 py-2">
                  <AttachmentsRow items={attachments.items} onRemove={attachments.remove} />
                </div>
              ) : null}
            </div>
            {menuOpen && matches.length > 0 ? (
              <div className="absolute left-0 top-full z-30 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-border bg-background shadow-lg">
                {matches.map((t, i) => (
                  <button
                    key={t.id}
                    type="button"
                    onMouseEnter={() => setMenuActiveIdx(i)}
                    onClick={() => applyTemplate(t)}
                    className={cn(
                      'flex w-full flex-col gap-0.5 border-b border-border/40 px-2 py-1.5 text-left text-xs last:border-b-0',
                      i === menuActiveIdx ? 'bg-accent' : 'hover:bg-accent/50',
                    )}
                  >
                    <span className="font-medium">{t.name}</span>
                    {/* Decode HTML → plain text for the slash-menu
                        preview line; the body is stored as HTML by the
                        rich template editor and showing the raw markup
                        was confusing. */}
                    <span className="truncate text-3xs text-muted-foreground">{htmlToPlain(t.body).slice(0, 100)}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {/* flex-wrap so the left-side hint can drop below the action
            cluster on phones — otherwise three buttons + the hint
            collide at 360px and either the Send button or the hint
            text gets clipped/overlapped. min-w-0 on the hint lets
            it shrink/truncate instead of forcing a horizontal scroll. */}
        <DialogFooter className="flex flex-row flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2 text-3xs text-muted-foreground">
            <Pencil className="h-3 w-3 shrink-0" />
            <span className="truncate">Sends from your address · signature appended</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={attachments.items.length >= attachments.maxFiles}
              title={`Attach files (≤25MB each, max ${attachments.maxFiles}). Paste / drag also works.`}
            >
              <Paperclip className="mr-1 h-3.5 w-3.5" />
              Attach
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files) attachments.add(e.target.files);
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={toggleAiBar}
              title="Tell the AI what to say, restyle your text, or use your saved prompts"
            >
              <SlidersHorizontal className="mr-1 h-3.5 w-3.5" />
              {aiBarOpen ? 'Hide AI options' : 'Guide AI'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => assist.draft()}
              disabled={assist.busy}
              title="Write the email from your notes. Open Guide AI to say what it should cover."
            >
              <Sparkles className="mr-1 h-3.5 w-3.5" />
              {assist.drafting ? 'Drafting…' : 'Draft'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => assist.polish()}
              disabled={assist.busy || isBodyEmpty}
              title="Rewrite professionally — preserves meaning"
            >
              <Wand2 className="mr-1 h-3.5 w-3.5" />
              {assist.polishing ? 'Polishing…' : 'Polish'}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={send}
              className="min-w-[6.5rem] gap-1"
            >
              <Send className="h-3.5 w-3.5" aria-hidden />
              Send
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
